import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, Loader2, Sparkles, School, Users, Presentation, Wallet } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCREEN_IMGS = [
  "/manual/dashboard.jpeg", "/manual/estudiantes.jpeg", "/manual/factura_consolidada.jpeg",
  "/manual/planificacion.jpeg", "/manual/nomina.jpeg", "/manual/asistente_ia.jpeg",
  "/manual/portal_maestro.jpeg", "/manual/portal_padre.jpeg", "/manual/ponche.jpeg",
];

const PROFILE_ICONS = [School, Presentation, Users, Wallet];

const fmt = (n) => `RD$ ${Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })}`;

export default function Presentacion() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ school_name: "", name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/proposal`).then((r) => r.json()).then(setData).catch(() => setData({ branding: {}, presentation: {}, plans: [], trial_days: 15 }));
  }, []);

  const brand = data?.branding || {};
  const pres = data?.presentation || {};
  const plans = data?.plans || [];
  const trialDays = data?.trial_days || 15;
  const name = brand.company_name || "EduNexa";
  const vars = { d: trialDays, n: name };

  const headline = pres.headline || t("pres.defaultHeadline");
  const subheadline = pres.subheadline || t("pres.defaultSub", vars);
  const ctaText = pres.cta || t("pres.ctaHero", vars);

  const profiles = t("pres.profiles", vars) || [];
  const steps = t("pres.steps", vars) || [];
  const screens = t("pres.screens") || [];
  const faq = t("pres.faq", vars) || [];
  const F = (k) => t(`pres.form.${k}`, vars);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/public/demo-signup`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Error");
      toast.success(t("pres.form.welcome", { d: d.trial_ends }));
      setTimeout(() => { window.location.href = "/dashboard"; }, 800);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="presentacion-page" style={{ fontFamily: "Outfit, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center gap-3">
          {(brand.icon_url || brand.logo_url) && (
            <img src={brand.icon_url || brand.logo_url} alt={name} className="h-9 w-9 rounded-lg object-contain" data-testid="presentacion-logo" />
          )}
          <span className="font-bold text-lg text-slate-900">{name}</span>
          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitcher testid="language-switcher-pres" />
            <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900 transition-colors" data-testid="presentacion-login">{t("pres.login")}</Link>
            <a href="#demo" className="bg-blue-800 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors" data-testid="presentacion-cta-top">
              {t("pres.ctaTop", vars)}
            </a>
          </div>
        </div>
      </header>

      <section className="relative bg-slate-900 text-white overflow-hidden">
        {brand.cover_url && <img src={brand.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="relative max-w-6xl mx-auto px-5 py-20 sm:py-28">
          {pres.badge && (
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-sky-500/15 text-sky-300 border border-sky-500/30 rounded-full px-4 py-1.5" data-testid="presentacion-badge">
              <Sparkles className="w-3.5 h-3.5" /> {pres.badge}
            </span>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-6 leading-tight max-w-3xl" data-testid="presentacion-headline">
            {headline}
          </h1>
          <p className="text-slate-300 text-lg mt-5 max-w-2xl" data-testid="presentacion-subheadline">
            {subheadline}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a href="#demo" className="bg-sky-400 hover:bg-sky-300 text-slate-900 font-bold px-7 py-3.5 rounded-full transition-colors" data-testid="presentacion-cta-hero">
              {ctaText}
            </a>
            <a href="#pantallas" className="border border-slate-600 hover:border-slate-400 text-slate-200 font-medium px-7 py-3.5 rounded-full transition-colors" data-testid="presentacion-ver-pantallas">
              {t("pres.seeScreens")}
            </a>
          </div>
          <p className="text-xs text-slate-400 mt-4">{t("pres.heroNote")}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t("pres.profilesTitle")}</h2>
        <p className="text-slate-500 mt-2 max-w-2xl">{t("pres.profilesSub", vars)}</p>
        <div className="grid md:grid-cols-2 gap-5 mt-8">
          {profiles.map((p, idx) => {
            const Icon = PROFILE_ICONS[idx] || School;
            return (
              <div key={idx} className="border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid={`profile-card-${idx}`}>
                <div className="w-11 h-11 rounded-xl bg-blue-800/10 text-blue-800 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900 mt-3">{p.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5">{p.desc}</p>
                <ul className="mt-4 space-y-2">
                  {(p.points || []).map((pt, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t("pres.stepsTitle")}</h2>
          <p className="text-slate-500 mt-2">{t("pres.stepsSub")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {steps.map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
                <span className="w-9 h-9 rounded-full bg-blue-800 text-white flex items-center justify-center font-bold">{i + 1}</span>
                <h3 className="font-semibold text-slate-900 mt-3">{s.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pantallas" className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t("pres.screensTitle")}</h2>
        <p className="text-slate-500 mt-2">{t("pres.screensSub")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="screens-gallery">
          {screens.map((s, i) => (
            <figure key={i} className="border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <a href={SCREEN_IMGS[i]} target="_blank" rel="noreferrer">
                <img src={SCREEN_IMGS[i]} alt={s.title} className="w-full aspect-video object-cover object-top" loading="lazy" />
              </a>
              <figcaption className="p-4">
                <p className="font-semibold text-slate-900 text-sm">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold">{t("pres.plansTitle")}</h2>
            <p className="text-slate-400 mt-2">{t("pres.plansSub", vars)}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-10 max-w-3xl mx-auto" data-testid="plans-section">
            {plans.map((p) => (
              <div key={p.id} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 flex flex-col" data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg">{p.name}</h3>
                  <span className="text-xs font-semibold bg-sky-400/15 text-sky-300 border border-sky-400/30 rounded-full px-3 py-1">{t("pres.freeDays", vars)}</span>
                </div>
                <p className="mt-3">
                  <span className="text-3xl font-bold">{fmt(p.price_per_student)}</span>{" "}
                  <span className="text-slate-400 text-sm">{p.periodicity === "Anual" ? t("pres.perStudentYearly") : t("pres.perStudentMonthly")}</span>
                </p>
                <p className="text-sm text-slate-400 mt-2 flex-1">{p.description || t("pres.allIncluded")}</p>
                <a href="#demo" className="mt-5 block text-center bg-sky-400 hover:bg-sky-300 text-slate-900 font-bold py-2.5 rounded-full transition-colors" data-testid={`plan-cta-${p.id}`}>
                  {t("pres.ctaPlan")}
                </a>
              </div>
            ))}
            {plans.length === 0 && <p className="text-slate-400 text-sm sm:col-span-2 text-center">{t("pres.noPlans")}</p>}
          </div>
        </div>
      </section>

      <section id="demo" className="max-w-6xl mx-auto px-5 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t("pres.demoTitle", vars)}</h2>
            <p className="text-slate-500 mt-2">{t("pres.demoSub")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="demo-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{F("school")}</label>
                <input required data-testid="demo-school-name" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                  value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} placeholder={F("schoolPh")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{F("name")}</label>
                  <input required data-testid="demo-name" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={F("namePh")} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{F("phone")}</label>
                  <input data-testid="demo-phone" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={F("phonePh")} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{F("email")}</label>
                <input required type="email" data-testid="demo-email" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={F("emailPh")} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{F("password")}</label>
                <input required type="password" minLength={6} data-testid="demo-password" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30"
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={F("passwordPh")} />
              </div>
              <button type="submit" disabled={busy} data-testid="demo-submit"
                className="w-full bg-blue-800 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {F("submit")}
              </button>
              <p className="text-xs text-slate-400 text-center">{F("legal")}</p>
            </form>
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t("pres.faqTitle")}</h2>
            <div className="mt-6 space-y-3" data-testid="faq-section">
              {faq.map((f, i) => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button type="button" className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                    data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    {f.q}
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && <p className="px-5 pb-4 text-sm text-slate-600">{f.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-wrap items-center gap-3 text-sm text-slate-500">
          {(brand.icon_url || brand.logo_url) && <img src={brand.icon_url || brand.logo_url} alt="" className="h-8 w-8 rounded-lg object-contain" />}
          <span className="font-semibold text-slate-700">{name}</span>
          <span>· {t("pres.footerTag")}</span>
          <span className="ml-auto flex gap-4">
            {(brand.phones || []).map((p) => <span key={p}>{p}</span>)}
            {(brand.emails || []).slice(0, 1).map((e) => <a key={e} href={`mailto:${e}`} className="hover:text-slate-800">{e}</a>)}
          </span>
        </div>
      </footer>
    </div>
  );
}
